import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import AdSlot from '../../components/AdSlot';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA, { usePlatform } from '../../components/StoreCTA';
import { appForPath, storeUrl, trackStoreClick } from '../../utils/storeLinks';
import useGameFrame from '../../account/useGameFrame';
import ProgressNote from '../../components/ProgressNote';
import './GamePage.css';

// Headline for the app CTA under each player.
const ctaHeadline = (appKey, gameName) => {
  if (appKey === 'crowns') return 'Love Crowns? Get 1,500+ Crowns puzzles and a daily puzzle in the free app';
  if (appKey === 'bsp') return 'Get 1,300+ Binary Sudoku puzzles in the free app';
  if (gameName === 'Daily Logic Puzzles') return "Keep your streak on your phone with the free Daily Logic Puzzles app";
  return `Enjoying ${gameName}? Get eight new logic puzzles every day in our free app`;
};

// Games that have printable versions
const printableGames = {
  crowns: '/printable/crowns',
  binarysudokuplus: '/printable/binary-sudoku-plus',
  aquarium: '/printable/aquarium',
  sudoku: '/printable/sudoku',
  dailylogicpuzzles: '/printable/tent-logic',
  nonograms: '/printable/nonograms',
  calcudoku: '/printable/calcudoku'
};

// Game-specific meta data configuration
const gameMetaData = {
  dailylogicpuzzles: {
    title: "Daily Logic Puzzles - Play a New Logic Challenge Every Day | Logic Puzzles CA",
    description: "Play Daily Logic Puzzles for free! A fresh set of bite-sized logic challenges designed to keep you sharp. Come back daily for a new puzzle and build your reasoning skills.",
    keywords: "daily logic puzzles, daily puzzles, logic puzzles online, brain teasers, deductive reasoning puzzles, daily brain training, free logic games",
    ogTitle: "Daily Logic Puzzles - A New Logic Challenge Every Day",
    ogDescription: "A fresh set of bite-sized logic puzzles every day. Free to play, mobile-friendly, and built for quick brain training.",
    twitterTitle: "Daily Logic Puzzles - A New Logic Challenge Every Day",
    twitterDescription: "Play a fresh set of bite-sized logic puzzles every day. Free to play and mobile-friendly.",
    canonicalUrl: "https://logicpuzzles.ca/play/dailylogicpuzzles",
    ogImage: "/dailylogicpuzzles/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Daily Logic Puzzles"
  },
  aquarium: {
    title: "Play Aquarium Puzzle Online - Free Water Logic Grid Game | Logic Puzzles CA",
    description: "Play Aquarium logic puzzles online for free! Challenge your mind with water-filling grid puzzles. Pure deductive reasoning with unique solutions. Perfect for puzzle enthusiasts who love brain teasers!",
    keywords: "Aquarium puzzle online, play aquarium logic puzzle, water puzzle game, free logic puzzles, brain teaser games, deductive reasoning puzzles, grid logic games",
    ogTitle: "Play Aquarium Puzzle Online - Free Water Logic Game",
    ogDescription: "Master the ultimate water logic challenge! Play Aquarium puzzles with physics-based constraints and pure deductive reasoning. Free online brain game for serious puzzle solvers.",
    twitterTitle: "Play Aquarium Puzzle Online - Free Water Logic Game",
    twitterDescription: "Challenge your logical thinking with Aquarium puzzles - pure deductive reasoning water-filling challenges. Play free now!",
    canonicalUrl: "https://logicpuzzles.ca/play/aquarium",
    ogImage: "/assets/images/gameplay/aquarium.png",
    ogImageWidth: "414",
    ogImageHeight: "495",
    gameName: "Aquarium"
  },
  crowns: {
    title: "Play Crowns Online - Free Logic Puzzle | Logic Puzzles CA",
    description: "Play the Crowns logic puzzle free online. Place one crown in every row, column and color region, with no two crowns touching. Unique solutions, no guessing.",
    keywords: "Crowns puzzle online, crowns logic puzzle, crown placement puzzle, free logic puzzles, brain teaser games",
    ogTitle: "Play Crowns Online - Free Logic Puzzle",
    ogDescription: "One crown per row, column and color region, and crowns can't touch. Play Crowns free in your browser.",
    twitterTitle: "Play Crowns Online - Free Logic Puzzle",
    twitterDescription: "One crown per row, column and color region, and crowns can't touch. Free Crowns logic puzzles.",
    canonicalUrl: "https://logicpuzzles.ca/play/crowns",
    ogImage: "/assets/images/gameplay/crowns.png",
    ogImageWidth: "513",
    ogImageHeight: "557",
    gameName: "Crowns"
  },
  binarysudokuplus: {
    title: "Play Binary Sudoku Plus Online - Free 0s and 1s Logic Game | Logic Puzzles CA",
    description: "Play Binary Sudoku Plus online for free! Challenge your mind with advanced binary logic puzzles. Pure deductive reasoning with 0s and 1s.",
    keywords: "Binary Sudoku online, play binary puzzle, 0s and 1s puzzle game, free logic puzzles, binary logic games",
    ogTitle: "Play Binary Sudoku Plus Online - Free Binary Logic Game",
    ogDescription: "Master the ultimate binary challenge! Play Binary Sudoku Plus with advanced rules and pure deductive reasoning.",
    twitterTitle: "Play Binary Sudoku Plus Online - Free Binary Logic Game",
    twitterDescription: "Challenge your logical thinking with Binary Sudoku Plus - advanced 0s and 1s puzzles.",
    canonicalUrl: "https://logicpuzzles.ca/play/binarysudoku",
    ogImage: "/assets/images/gameplay/binarysudokuplus.png",
    ogImageWidth: "617",
    ogImageHeight: "616",
    gameName: "Binary Sudoku Plus"
  },
  palettepath: {
    title: "Play Color Flood Online - Free Color Connection Logic Game | Logic Puzzles CA",
    description: "Play Color Flood logic puzzles online for free! Challenge your mind with color-matching grid puzzles. Pure deductive reasoning with unique solutions.",
    keywords: "Color Flood online, play color flood puzzle, color connection game, free logic puzzles, color logic games",
    ogTitle: "Play Color Flood Online - Free Color Logic Game",
    ogDescription: "Master the ultimate color challenge! Play Color Flood puzzles with connection rules and pure deductive reasoning.",
    twitterTitle: "Play Color Flood Online - Free Color Logic Game",
    twitterDescription: "Challenge your logical thinking with Color Flood puzzles - color connection challenges.",
    canonicalUrl: "https://logicpuzzles.ca/play/palettepath",
    ogImage: "/assets/images/gameplay/palettepath.png",
    ogImageWidth: "617",
    ogImageHeight: "616",
    gameName: "Color Flood: Logic Puzzle"
  },
  dotstream: {
    title: "Play Dot Stream Online - Free Path Connection Logic Game | Logic Puzzles CA",
    description: "Play Dot Stream logic puzzles online for free! Challenge your mind with path-finding grid puzzles. Pure deductive reasoning with unique solutions.",
    keywords: "Dot Stream online, play dot stream puzzle, path connection game, free logic puzzles, stream logic games",
    ogTitle: "Play Dot Stream Online - Free Path Logic Game",
    ogDescription: "Master the ultimate path challenge! Play Dot Stream puzzles with connection rules and pure deductive reasoning.",
    twitterTitle: "Play Dot Stream Online - Free Path Logic Game",
    twitterDescription: "Challenge your logical thinking with Dot Stream puzzles - path connection challenges.",
    canonicalUrl: "https://logicpuzzles.ca/play/dotstream",
    ogImage: "/assets/images/gameplay/dotstream.png",
    ogImageWidth: "617",
    ogImageHeight: "616",
    gameName: "Dot Stream"
  },
  foxhedge: {
    title: "Play Fox Hedge Online - Daily Pen Building Puzzle | Logic Puzzles CA",
    description: "Play Fox Hedge for free! Plant a limited number of hedges to pen the fox in the best meadow you can. Fit in mushrooms, keep out the hens and match par for three stars.",
    keywords: "fox hedge, pen building puzzle, hedge puzzle, grid logic puzzle, daily puzzle game",
    ogTitle: "Fox Hedge - Daily Pen Building Puzzle",
    ogDescription: "Plant limited hedges to pen the fox. Mushrooms score, hens cost, and every daily puzzle has a computer-checked par.",
    twitterTitle: "Fox Hedge - Daily Pen Building Puzzle",
    twitterDescription: "Pen the fox with a limited number of hedges in this daily grid puzzle.",
    canonicalUrl: "https://logicpuzzles.ca/play/foxhedge",
    ogImage: "/assets/images/gameplay/fox-hedge.png",
    ogImageWidth: "1024",
    ogImageHeight: "1024",
    gameName: "Fox Hedge"
  },
  nonograms: {
    title: "Play Nonograms Online - Free Picture Logic Puzzle Game | Logic Puzzles CA",
    description: "Play Nonograms online for free! Reveal hidden pictures by filling cells using row and column number clues. 512 pictures from 5x5 to 20x20, plus a daily picture.",
    keywords: "nonogram online, play nonograms free, picture logic puzzle, paint by number logic puzzle, free nonogram",
    ogTitle: "Play Nonograms Online - Free Picture Logic Puzzle",
    ogDescription: "Reveal hidden pictures using logic! Play 512 free Nonogram pictures in 16 packs, plus a daily picture.",
    twitterTitle: "Play Nonograms Online - Free Picture Logic Puzzle",
    twitterDescription: "Reveal hidden pictures by filling cells using number clues. 512 free puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/nonograms",
    ogImage: "/nonograms/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Nonograms"
  },
  calcudoku: {
    title: "Play Calcudoku Online - Free Math Logic Puzzle Game | Logic Puzzles CA",
    description: "Play Calcudoku online for free! Combine arithmetic with logic to fill the grid. 560 puzzles from 3x3 to 9x9 with cage constraints, plus a daily puzzle.",
    keywords: "calcudoku online, play calcudoku free, math logic puzzle, arithmetic puzzle game, calcudoku solver, free calcudoku",
    ogTitle: "Play Calcudoku Online - Free Math Logic Puzzle",
    ogDescription: "Combine arithmetic with logic! Play 560 free Calcudoku puzzles from 3x3 to 9x9.",
    twitterTitle: "Play Calcudoku Online - Free Math Logic Puzzle",
    twitterDescription: "Combine math and logic in Calcudoku puzzles. Fill grids using cage constraints. 560 free puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/calcudoku",
    ogImage: "/calcudoku/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Calcudoku"
  },
  wordwheel: {
    title: "Play Word Wheel Online - Free 60-Second Word Game | Logic Puzzles CA",
    description: "Play Word Wheel online for free! Find as many words as you can in 60 seconds using 9 letters on a wheel. Swipe to connect letters and build words. Daily puzzles with leaderboards.",
    keywords: "word wheel online, word game free, anagram puzzle, letter wheel game, daily word puzzle, 60 second word game",
    ogTitle: "Play Word Wheel Online - Free 60-Second Word Game",
    ogDescription: "Find as many words as you can in 60 seconds! Swipe across 9 letters on a wheel to build words. New daily puzzle every day.",
    twitterTitle: "Play Word Wheel Online - Free 60-Second Word Game",
    twitterDescription: "Find words in 60 seconds using 9 letters on a wheel. Daily puzzles with leaderboards!",
    canonicalUrl: "https://logicpuzzles.ca/play/wordwheel",
    ogImage: "/wordwheel/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Word Wheel"
  },
  sudoku: {
    title: "Play Sudoku Online - Free Classic Number Logic Game | Logic Puzzles CA",
    description: "Play Sudoku online for free: 1,200 hand-graded puzzles in four difficulty packs, a new daily board every day, notes and step-by-step hints. Every puzzle has one solution and needs no guessing.",
    keywords: "Sudoku online, play sudoku free, classic sudoku game, number logic puzzles, brain teaser games",
    ogTitle: "Play Sudoku Online - Free Classic Number Game",
    ogDescription: "Classic 9x9 Sudoku with four difficulty packs, a daily board, notes and step-by-step hints.",
    twitterTitle: "Play Sudoku Online - Free Classic Number Game",
    twitterDescription: "Challenge your mind with classic Sudoku - the timeless number logic puzzle.",
    canonicalUrl: "https://logicpuzzles.ca/play/sudoku",
    ogImage: "/og-image.png",
    ogImageWidth: "1200",
    ogImageHeight: "630",
    gameName: "Sudoku"
  },
  minefinder: {
    title: "Play Mine Finder Online - Free No-Guess Logic Puzzle | Logic Puzzles CA",
    description: "Play Mine Finder online for free! A mine-finding logic puzzle where every board is solvable without guessing. Use number clues to find the hidden mines and clear the board.",
    keywords: "mine finder online, mine puzzle free, no guess mine game, number clue puzzle, logic deduction game",
    ogTitle: "Play Mine Finder Online - Free No-Guess Logic Puzzle",
    ogDescription: "Clear the minefield using logic! Use number clues to find hidden mines in this classic puzzle game.",
    twitterTitle: "Play Mine Finder Online - Free No-Guess Logic Puzzle",
    twitterDescription: "A mine-finding logic puzzle with no guessing. Use number clues to clear the board safely!",
    canonicalUrl: "https://logicpuzzles.ca/play/minefinder",
    ogImage: "/minefinder/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Mine Finder"
  },
  tents: {
    title: "Play Tent Logic Online - Free Camping Logic Puzzle | Logic Puzzles CA",
    description: "Play Tent Logic puzzles online for free! Pitch a tent beside every tree using row and column clues. Each tree gets one tent, and no two tents touch - even diagonally.",
    keywords: "tent logic online, tent puzzle free, camping logic puzzle, campsite puzzle, grid logic game",
    ogTitle: "Play Tent Logic Online - Free Camping Logic Puzzle",
    ogDescription: "Place tents next to trees on the grid! Use row and column clues in this relaxing logic puzzle.",
    twitterTitle: "Play Tent Logic Online - Free Camping Logic Puzzle",
    twitterDescription: "Place tents next to trees using logic clues. A relaxing grid puzzle for all skill levels!",
    canonicalUrl: "https://logicpuzzles.ca/play/tents",
    ogImage: "/tents/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Tent Logic"
  },
  colorlinks: {
    title: "Play Color Links Online - Free Path Connection Puzzle | Logic Puzzles CA",
    description: "Play Color Links online for free! Connect matching colored dots by drawing paths that fill the entire grid. No crossing, no empty cells - pure logic!",
    keywords: "color links online, play color links free, path connection puzzle, path connection game, dot connecting puzzle, grid logic game",
    ogTitle: "Play Color Links Online - Free Path Connection Puzzle",
    ogDescription: "Connect matching colors to fill the grid! Draw paths between colored dots without crossing lines.",
    twitterTitle: "Play Color Links Online - Free Path Connection Puzzle",
    twitterDescription: "Connect matching colors to fill the grid. A satisfying path puzzle for all skill levels!",
    canonicalUrl: "https://logicpuzzles.ca/play/colorlinks",
    ogImage: "/assets/logos/colorlinks_logo.svg",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Color Links"
  },
  nurikabe: {
    title: "Play Nurikabe Online - Free Island Logic Puzzle | Logic Puzzles CA",
    description: "Play Nurikabe puzzles online for free! Shade sea cells to form a connected ocean while isolating numbered islands. No 2x2 sea blocks allowed. 500 puzzles across 3 difficulty levels.",
    keywords: "nurikabe online, play nurikabe free, island puzzle, sea logic puzzle, shading puzzle, grid logic game",
    ogTitle: "Play Nurikabe Online - Free Island Logic Puzzle",
    ogDescription: "Shade the sea and isolate the islands! Use number clues to solve this classic Japanese logic puzzle.",
    twitterTitle: "Play Nurikabe Online - Free Island Logic Puzzle",
    twitterDescription: "Shade sea cells and isolate islands using number clues. 500 free puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/nurikabe",
    ogImage: "/assets/logos/nurikabe_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Nurikabe"
  },
  fleet: {
    title: "Play Fleet Online - Free Ship Placement Logic Puzzle | Logic Puzzles CA",
    description: "Play Fleet ship-placement puzzles online for free! Place ships on the grid using row and column clues. Ships can't touch each other, even diagonally. 500 puzzles across 3 difficulty levels.",
    keywords: "fleet puzzle online, hidden fleet puzzle, ship placement puzzle, naval logic game, grid puzzle free",
    ogTitle: "Play Fleet Online - Free Ship Placement Puzzle",
    ogDescription: "Place the fleet on the grid using row and column clues! Ships can't touch - even diagonally.",
    twitterTitle: "Play Fleet Online - Free Ship Placement Puzzle",
    twitterDescription: "Place ships on the grid using number clues. A classic naval logic puzzle with 500 free levels!",
    canonicalUrl: "https://logicpuzzles.ca/play/fleet",
    ogImage: "/assets/logos/fleet_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Fleet"
  },
  lightup: {
    title: "Play Light Up Online - Free Illumination Logic Puzzle | Logic Puzzles CA",
    description: "Play Light Up puzzles online for free! Place light bulbs to illuminate every cell without any two bulbs shining on each other. 500 puzzles across 3 difficulty levels.",
    keywords: "light up puzzle online, light bulb puzzle, illumination logic game, bulb placement puzzle, grid logic puzzle free",
    ogTitle: "Play Light Up Online - Free Illumination Puzzle",
    ogDescription: "Place bulbs to light up every cell! No two bulbs can see each other in this classic logic puzzle.",
    twitterTitle: "Play Light Up Online - Free Illumination Puzzle",
    twitterDescription: "Place light bulbs to illuminate the grid. No conflicts allowed! 500 free puzzles.",
    canonicalUrl: "https://logicpuzzles.ca/play/lightup",
    ogImage: "/assets/logos/lightup_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Light Up"
  },
  wordladder: {
    title: "Play Word Ladder Online - Free Word Transformation Puzzle | Logic Puzzles CA",
    description: "Play Word Ladder puzzles online for free! Change one letter at a time to transform the starting word into the target word. A classic vocabulary and logic challenge.",
    keywords: "word ladder online, play word ladder free, word transformation game, letter change puzzle, vocabulary game",
    ogTitle: "Play Word Ladder Online - Free Word Transformation Puzzle",
    ogDescription: "Climb the puzzle one letter at a time! Transform words step by step in this classic word game.",
    twitterTitle: "Play Word Ladder Online - Free Word Transformation Puzzle",
    twitterDescription: "Change one letter per step to reach the target word. A classic vocabulary puzzle!",
    canonicalUrl: "https://logicpuzzles.ca/play/wordladder",
    ogImage: "/wordladder/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Word Ladder"
  },
  cryptograms: {
    title: "Play Cryptograms Online - Free Letter Substitution Puzzle | Logic Puzzles CA",
    description: "Decode famous quotes by cracking the substitution cipher! Each letter stands for another in this classic word puzzle. Free to play with 500 puzzles.",
    keywords: "cryptograms online, cipher puzzles, letter substitution, decode quotes, word puzzles, brain teasers, free cryptogram games",
    ogTitle: "Play Cryptograms Online - Decode Hidden Quotes",
    ogDescription: "Crack the code and reveal famous quotes! Free cryptogram puzzles with 500 levels of cipher-cracking fun.",
    twitterTitle: "Play Cryptograms Online - Decode Hidden Quotes",
    twitterDescription: "Crack substitution ciphers to reveal famous quotes. 500 free puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/cryptograms",
    ogImage: "/cryptograms/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Cryptograms"
  },
  futoshiki: {
    title: "Play Futoshiki Online - Free Inequality Number Puzzle | Logic Puzzles CA",
    description: "Play Futoshiki puzzles online for free! Fill the grid with numbers while following greater-than and less-than clues. Pure logic, no guessing required.",
    keywords: "futoshiki online, inequality puzzle, number logic game, greater than less than puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Futoshiki Online - Inequality Number Puzzle",
    ogDescription: "Fill the grid using inequality clues! A satisfying number puzzle that's all logic, no guessing. 500 free levels.",
    twitterTitle: "Play Futoshiki Online - Inequality Number Puzzle",
    twitterDescription: "Number placement meets inequality constraints. 500 free Futoshiki puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/futoshiki",
    ogImage: "/assets/logos/futoshiki_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Futoshiki"
  },
  galaxies: {
    title: "Play Galaxies Online - Free Symmetry Region Puzzle | Logic Puzzles CA",
    description: "Divide the grid into rotationally symmetric galaxies! Each region must have perfect 180-degree symmetry around its center dot. 500 free puzzles.",
    keywords: "galaxies puzzle online, tentai show, symmetry puzzle, region division puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Galaxies Online - Symmetry Region Puzzle",
    ogDescription: "Create rotationally symmetric regions around galaxy centers. A beautiful logic puzzle with 500 free levels.",
    twitterTitle: "Play Galaxies Online - Symmetry Region Puzzle",
    twitterDescription: "Divide the grid into symmetric galaxies. 500 free logic puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/galaxies",
    ogImage: "/assets/logos/galaxies_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Galaxies"
  },
  kurodoku: {
    title: "Play Kurodoku Online - Free Black Cell Logic Puzzle | Logic Puzzles CA",
    description: "Play Kurodoku puzzles online for free! Shade cells black while keeping white cells connected. Numbers show visibility in four directions. Pure deduction!",
    keywords: "kurodoku online, black cell puzzle, visibility logic puzzle, shading puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Kurodoku Online - Black Cell Logic Puzzle",
    ogDescription: "Shade cells using visibility clues while keeping white cells connected. A satisfying deduction puzzle with 500 free levels.",
    twitterTitle: "Play Kurodoku Online - Black Cell Logic Puzzle",
    twitterDescription: "Use visibility numbers to shade cells in this elegant logic puzzle. 500 free levels!",
    canonicalUrl: "https://logicpuzzles.ca/play/kurodoku",
    ogImage: "/assets/logos/kurodoku_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Kurodoku"
  },
  logicgridpuzzles: {
    title: "Play Logic Grid Puzzles Online - Free Zebra Puzzle Riddles | Logic Puzzles CA",
    description: "Solve classic logic grid puzzles online for free! Use clues to match items across categories in these zebra-puzzle riddles. 400 puzzles from easy to expert, plus a daily case.",
    keywords: "logic grid puzzles, logic grid riddle, elimination grid, deduction puzzles, free logic games, brain teasers, zebra puzzle",
    ogTitle: "Play Logic Grid Puzzles Online - Zebra Puzzle Style",
    ogDescription: "Classic elimination grid puzzles! Use clues to deduce which items match across categories. 400 free puzzles.",
    twitterTitle: "Play Logic Grid Puzzles Online - Zebra Puzzle Style",
    twitterDescription: "Classic logic grid elimination puzzles. 400 free zebra-puzzle riddles!",
    canonicalUrl: "https://logicpuzzles.ca/play/logicgridpuzzles",
    ogImage: "/logicgridpuzzles/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Logic Grid Puzzles"
  },
  mosaic: {
    title: "Play Mosaic Online - Free Neighborhood Count Puzzle | Logic Puzzles CA",
    description: "Play Mosaic puzzles online for free! Fill cells where each number shows how many filled cells are in its 3x3 block, and a hidden picture appears. 360 puzzles plus a daily puzzle.",
    keywords: "mosaic puzzle online, neighborhood count puzzle, picture logic puzzle, number clue puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Mosaic Online - Neighborhood Count Puzzle",
    ogDescription: "Numbers tell you how many filled cells surround them. Deduce the picture in this satisfying logic puzzle! 360 free levels.",
    twitterTitle: "Play Mosaic Online - Neighborhood Count Puzzle",
    twitterDescription: "Count the neighbors and reveal the picture! 360 free Mosaic puzzles.",
    canonicalUrl: "https://logicpuzzles.ca/play/mosaic",
    ogImage: "/mosaic/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Mosaic"
  },
  rectangles: {
    title: "Play Rectangles Online - Free Area Logic Puzzle | Logic Puzzles CA",
    description: "Play Rectangles online for free! Divide the grid into rectangles so each holds exactly one number equal to its area. 1,000 puzzles from 5x5 to 15x15, a daily puzzle and step-by-step hints.",
    keywords: "rectangles puzzle online, area logic puzzle, divide the grid puzzle, grid division puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Rectangles Online - Area Logic Puzzle",
    ogDescription: "Split the grid into rectangles so every number is an area. 1,000 free puzzles and a daily board.",
    twitterTitle: "Play Rectangles Online - Area Logic Puzzle",
    twitterDescription: "Split the grid so every number is an area. 1,000 free Rectangles puzzles.",
    canonicalUrl: "https://logicpuzzles.ca/play/rectangles",
    ogImage: "/rectangles/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Rectangles"
  },
  pipes: {
    title: "Play Pipes Online - Free Pipe Connection Puzzle | Logic Puzzles CA",
    description: "Rotate pipe pieces to connect them all into one network! No loose ends allowed in this classic logic puzzle. 500 free levels from easy to expert.",
    keywords: "pipes puzzle online, pipe connection game, plumber puzzle, network puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Pipes Online - Connect All Pipe Pieces",
    ogDescription: "Rotate pipe pieces to form one connected network with no loose ends. A classic logic puzzle with 500 free levels.",
    twitterTitle: "Play Pipes Online - Connect All Pipe Pieces",
    twitterDescription: "Rotate pipes to connect them all. 500 free pipe puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/pipes",
    ogImage: "/assets/logos/pipes_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Pipes"
  },
  renzoku: {
    title: "Play Renzoku Online - Free Consecutive Number Puzzle | Logic Puzzles CA",
    description: "Play Renzoku puzzles online for free! Fill the grid with numbers where dots show consecutive pairs. No dot means not consecutive. Pure logic deduction!",
    keywords: "renzoku online, consecutive number puzzle, dot logic puzzle, number placement, free logic puzzles, brain teasers",
    ogTitle: "Play Renzoku Online - Consecutive Number Puzzle",
    ogDescription: "Dots mark consecutive number pairs in this elegant number placement puzzle. 500 free levels of pure deduction!",
    twitterTitle: "Play Renzoku Online - Consecutive Number Puzzle",
    twitterDescription: "Dots mean consecutive, no dot means not. 500 free Renzoku puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/renzoku",
    ogImage: "/assets/logos/renzoku_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Renzoku"
  },
  slant: {
    title: "Play Slant Online - Free Diagonal Line Puzzle | Logic Puzzles CA",
    description: "Place diagonal lines in every cell! Numbers at intersections show how many lines meet there. No closed loops allowed. 500 free puzzles.",
    keywords: "slant puzzle online, diagonal line puzzle, intersection number puzzle, free logic puzzles, brain teasers, gokigen",
    ogTitle: "Play Slant Online - Diagonal Line Puzzle",
    ogDescription: "Fill every cell with a diagonal line using intersection numbers as clues. No loops allowed! 500 free levels.",
    twitterTitle: "Play Slant Online - Diagonal Line Puzzle",
    twitterDescription: "Place diagonals using intersection clues. 500 free Slant puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/slant",
    ogImage: "/assets/logos/slant_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Slant"
  },
  yinyang: {
    title: "Play Yin-Yang Online - Free Black & White Circle Puzzle | Logic Puzzles CA",
    description: "Fill every cell with a black or white circle! Both colors must stay connected, and no 2x2 area can be all one color. 500 free logic puzzles.",
    keywords: "yin yang puzzle online, black white circle puzzle, connectivity puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Yin-Yang Online - Black & White Circle Puzzle",
    ogDescription: "Balance black and white circles on the grid. Both colors must stay connected! 500 free levels.",
    twitterTitle: "Play Yin-Yang Online - Black & White Circle Puzzle",
    twitterDescription: "Black and white circles, both must stay connected. 500 free Yin-Yang puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/yinyang",
    ogImage: "/assets/logos/yinyang_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Yin-Yang"
  },
  skyscrapers: {
    title: "Play Skyscrapers Online - Free Height Placement Logic Puzzle | Logic Puzzles CA",
    description: "Play Skyscrapers puzzles online for free! Place buildings of varying heights so each row and column contains every height exactly once. Use edge visibility clues to solve!",
    keywords: "skyscrapers puzzle online, height placement puzzle, visibility logic puzzle, building puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Skyscrapers Online - Height Placement Puzzle",
    ogDescription: "Place buildings using visibility clues from the edges. Each row and column has every height exactly once! 500 free levels.",
    twitterTitle: "Play Skyscrapers Online - Height Placement Puzzle",
    twitterDescription: "Use edge clues to place buildings of different heights. 500 free Skyscrapers puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/skyscrapers",
    ogImage: "/assets/logos/skyscrapers_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Skyscrapers"
  },
  thermometers: {
    title: "Play Thermometers Online - Free Mercury Fill Logic Puzzle | Logic Puzzles CA",
    description: "Play Thermometers puzzles online for free! Fill thermometers to the right level using row and column clues. Each thermometer fills from its bulb. 500 free puzzles.",
    keywords: "thermometers puzzle online, mercury fill puzzle, temperature logic puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Thermometers Online - Mercury Fill Puzzle",
    ogDescription: "Fill thermometers from the bulb using row and column clues. A satisfying logic puzzle with 500 free levels!",
    twitterTitle: "Play Thermometers Online - Mercury Fill Puzzle",
    twitterDescription: "Fill thermometers to the right level using number clues. 500 free puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/thermometers",
    ogImage: "/assets/logos/thermometers_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "Thermometers"
  },
  dominosa: {
    title: "Play Domino Puzzle Online - Free Domino Logic Game | Logic Puzzles CA",
    description: "Play Domino Puzzle online for free! Place every domino so each colored region keeps its rule, or split a number grid into a full set of dominoes. Three new daily puzzles every day.",
    keywords: "domino puzzle online, domino logic game, domino placement puzzle, number pair puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play Domino Puzzle Online - Free Domino Logic Game",
    ogDescription: "Place the tiles, follow the rules. 360 region puzzles, 500 classic domino grids and three dailies a day, all free.",
    twitterTitle: "Play Domino Puzzle Online - Free Domino Logic Game",
    twitterDescription: "Place every domino so each region keeps its rule. 860 free puzzles plus three dailies a day!",
    canonicalUrl: "https://logicpuzzles.ca/play/dominosa",
    ogImage: "/dominosa/icons/Icon-512.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Domino Puzzle"
  },
  norinori: {
    title: "Play NoriNori Online - Free Domino Shading Logic Puzzle | Logic Puzzles CA",
    description: "Play NoriNori puzzles online for free! Shade exactly two cells in each region to form dominoes. No two shaded areas can touch, even diagonally. 500 free puzzles.",
    keywords: "norinori puzzle online, domino shading puzzle, region logic puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play NoriNori Online - Domino Shading Puzzle",
    ogDescription: "Shade two cells per region to form dominoes. No touching allowed! A unique logic puzzle with 500 free levels.",
    twitterTitle: "Play NoriNori Online - Domino Shading Puzzle",
    twitterDescription: "Shade dominoes in each region - no touching allowed. 500 free NoriNori puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/norinori",
    ogImage: "/assets/logos/norinori_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "NoriNori"
  },
  lits: {
    title: "Play LITS Online - Free Tetromino Logic Puzzle | Logic Puzzles CA",
    description: "Play LITS puzzles online for free! Place one tetromino (L, I, T, or S) in each region. Shaded cells must connect, no 2x2 blocks, and identical shapes can't touch!",
    keywords: "lits puzzle online, tetromino puzzle, shape placement puzzle, free logic puzzles, brain teasers",
    ogTitle: "Play LITS Online - Tetromino Logic Puzzle",
    ogDescription: "Place L, I, T, or S tetrominoes in each region. All shaded cells connect but no 2x2 blocks! 500 free levels.",
    twitterTitle: "Play LITS Online - Tetromino Logic Puzzle",
    twitterDescription: "Place tetrominoes in regions with clever constraints. 500 free LITS puzzles!",
    canonicalUrl: "https://logicpuzzles.ca/play/lits",
    ogImage: "/assets/logos/lits_logo.svg",
    ogImageWidth: "200",
    ogImageHeight: "200",
    gameName: "LITS"
  },
  euchre: {
    title: "Play Euchre Online - Free Classic Trick-Taking Card Game | Logic Puzzles CA",
    description: "Play Euchre online for free! Master the classic trick-taking card game. Form partnerships, call trump, and outsmart your opponents in this fast-paced four-player card game.",
    keywords: "euchre online, euchre card game, trick-taking game, free card games, trump card game, partnership card game",
    ogTitle: "Play Euchre Online - Classic Trick-Taking Card Game",
    ogDescription: "Play Euchre online! A fast-paced trick-taking card game. Form partnerships and call trump strategically. Free to play!",
    twitterTitle: "Play Euchre Online - Classic Trick-Taking Card Game",
    twitterDescription: "Play Euchre online! Master the classic trick-taking card game. Free to play!",
    canonicalUrl: "https://logicpuzzles.ca/play/euchre",
    ogImage: "/euchre/assets/assets/card_logo.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Euchre"
  },
  butterflymahjong: {
    title: "Play Butterfly Tiles Online - Free Tile Matching Puzzle | Logic Puzzles CA",
    description: "Play Butterfly Tiles online for free! Match pairs of beautiful butterfly tiles to clear the board. Features zen and arcade modes with relaxing garden backgrounds.",
    keywords: "butterfly tiles online, mahjong solitaire, tile matching game, free puzzle game, mahjong connect, butterfly puzzle",
    ogTitle: "Play Butterfly Tiles Online - Free Tile Matching Puzzle",
    ogDescription: "Match butterfly tile pairs to clear the board! Zen and arcade modes with beautiful garden backgrounds. Free to play!",
    twitterTitle: "Play Butterfly Tiles Online - Free Tile Matching Puzzle",
    twitterDescription: "Match beautiful butterfly tiles in this relaxing mahjong solitaire game. Free to play!",
    canonicalUrl: "https://logicpuzzles.ca/play/butterflymahjong",
    ogImage: "/assets/logos/butterflymahjong_logo.png",
    ogImageWidth: "512",
    ogImageHeight: "512",
    gameName: "Butterfly Tiles"
  },
};

// Default meta data for games not specifically configured
const defaultMetaData = {
  title: "Play Logic Puzzles Online - Free Brain Games & Puzzle Collection | Logic Puzzles CA",
  description: "Play free online logic puzzles and brain games. Challenge your mind with our collection of engaging puzzle games. Perfect for all ages!",
  keywords: "logic puzzles online, free brain games, puzzle collection, mind games, logic games",
  ogTitle: "Play Logic Puzzles Online - Free Brain Games",
  ogDescription: "Challenge your mind with our collection of free online logic puzzles and brain games.",
  twitterTitle: "Play Logic Puzzles Online - Free Brain Games",
  twitterDescription: "Challenge your mind with free online logic puzzles and brain games.",
  canonicalUrl: "https://logicpuzzles.ca",
  ogImage: "/og-image.png",
  ogImageWidth: "1200",
  ogImageHeight: "630",
  gameName: "Logic Puzzles"
};

function GamePage({ gamePath }) {
  const navRef = useRef(null);
  const gameContainerRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeVisible, setIframeVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const platform = usePlatform();
  const appKey = appForPath(`/play/${gamePath}`);
  const storePage = `/play/${gamePath}`;

  // Get meta data for the specific game or use default with correct canonical
  const metaData = gameMetaData[gamePath] || {
    ...defaultMetaData,
    canonicalUrl: `https://logicpuzzles.ca/play/${gamePath}`,
  };
  const frame = useGameFrame(`/${gamePath}/index.html`, gamePath, metaData.gameName);

  // Generate breadcrumb structured data
  const generateBreadcrumbData = () => {
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://logicpuzzles.ca"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Logic Games",
          "item": "https://logicpuzzles.ca/logic-games"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": `Play ${metaData.gameName}`,
          "item": metaData.canonicalUrl
        }
      ]
    };
    return JSON.stringify(breadcrumbData);
  };

  // Generate structured data for the game
  const generateGameStructuredData = () => {
    const baseStructuredData = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": metaData.ogTitle,
      "description": metaData.description,
      "url": metaData.canonicalUrl,
      "image": `https://logicpuzzles.ca${metaData.ogImage}`,
      "genre": "Logic Puzzle",
      "applicationCategory": "Game",
      "operatingSystem": ["Windows", "macOS", "Linux", "iOS", "Android"],
      "browserRequirements": "HTML5 compatible browser",
      "isAccessibleForFree": true,
      "gamePlatform": "Web Browser",
      "publisher": {
        "@type": "Organization",
        "name": "Logic Puzzles CA",
        "url": "https://logicpuzzles.ca",
        "logo": "https://logicpuzzles.ca/android-icon-192x192.png"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "CAD",
        "availability": "https://schema.org/InStock"
      },
      "audience": {
        "@type": "Audience",
        "audienceType": "Puzzle Enthusiasts"
      },
      "educationalUse": "Brain Training",
      "learningResourceType": "Logic Puzzle"
    };

    // Add game-specific categories and keywords
    if (gamePath === 'aquarium') {
      baseStructuredData.about = ["Water Logic Puzzles", "Grid Puzzles", "Deductive Reasoning"];
      baseStructuredData.keywords = "aquarium puzzle, water logic, grid puzzle, brain teaser";
      baseStructuredData.gameItem = "Water placement puzzle";
    } else if (gamePath === 'dailylogicpuzzles') {
      baseStructuredData.about = ["Daily Logic Puzzles", "Brain Training", "Deductive Reasoning"];
      baseStructuredData.keywords = "daily logic puzzles, daily puzzles, brain training, logic games";
      baseStructuredData.gameItem = "Daily puzzle collection";
    } else if (gamePath === 'crowns') {
      baseStructuredData.about = ["Crowns Puzzles", "Grid Puzzles", "Logic Games"];
      baseStructuredData.keywords = "crowns puzzle, crown placement, grid puzzle, brain teaser";
      baseStructuredData.gameItem = "Crown placement puzzle";
    } else if (gamePath === 'binarysudokuplus') {
      baseStructuredData.about = ["Binary Puzzles", "Sudoku Variants", "Logic Games"];
      baseStructuredData.keywords = "binary sudoku, 0s and 1s puzzle, logic puzzle, brain teaser";
      baseStructuredData.gameItem = "Binary logic puzzle";
    } else if (gamePath === 'palettepath') {
      baseStructuredData.about = ["Color Puzzles", "Path Puzzles", "Logic Games"];
      baseStructuredData.keywords = "color flood, color connection, path puzzle, brain teaser";
      baseStructuredData.gameItem = "Color path connection puzzle";
    } else if (gamePath === 'dotstream') {
      baseStructuredData.about = ["Path Puzzles", "Connection Puzzles", "Logic Games"];
      baseStructuredData.keywords = "dot stream, path connection, stream puzzle, brain teaser";
      baseStructuredData.gameItem = "Stream connection puzzle";
    } else if (gamePath === 'foxhedge') {
      baseStructuredData.about = ["Pen Building Puzzles", "Grid Puzzles", "Logic Games"];
      baseStructuredData.keywords = "fox hedge, hedge placement, grid puzzle, brain teaser";
      baseStructuredData.gameItem = "Pen building puzzle";
    } else if (gamePath === 'nurikabe') {
      baseStructuredData.about = ["Island Puzzles", "Shading Puzzles", "Logic Games"];
      baseStructuredData.keywords = "nurikabe puzzle, island logic, sea shading, grid puzzle";
      baseStructuredData.gameItem = "Island shading puzzle";
    } else if (gamePath === 'fleet') {
      baseStructuredData.about = ["Ship Puzzles", "Placement Puzzles", "Logic Games"];
      baseStructuredData.keywords = "fleet puzzle, ship placement, naval logic, grid puzzle";
      baseStructuredData.gameItem = "Ship placement puzzle";
    } else if (gamePath === 'lightup') {
      baseStructuredData.about = ["Illumination Puzzles", "Bulb Placement Puzzles", "Logic Games"];
      baseStructuredData.keywords = "light up puzzle, illumination logic, bulb placement";
      baseStructuredData.gameItem = "Bulb illumination puzzle";
    }

    return JSON.stringify(baseStructuredData);
  };

  useEffect(() => {
    if (navRef.current) {
      setNavHeight(navRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // The prerendered iframe starts loading before React hydrates. A light game
  // can finish first, and then React never sees its load event, so the
  // spinner would stay up forever. Catch that case once we've mounted.
  useEffect(() => {
    const el = frame.ref.current;
    try {
      const doc = el && el.contentDocument;
      if (doc && doc.readyState === 'complete' && (doc.location.pathname === `/${gamePath}` || doc.location.pathname.startsWith(`/${gamePath}/`))) {
        frame.onLoad(handleIframeLoad)({ target: el });
      }
    } catch (e) {
      /* cross-origin or not ready: the normal onLoad will fire */
    }
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIframeLoad = () => {
    // Keep the loading state in the build-time prerender so the static HTML
    // matches the first client render (spinner shown, iframe hidden).
    if (window.__PRERENDER__) return;
    setIframeVisible(true);
    setIsLoading(false);
    
    // Track game load event in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'game_load', {
        'game_name': gamePath,
        'event_category': 'engagement',
        'event_label': gamePath
      });
    }
  };

  const handleFullscreenToggle = async () => {
    const container = gameContainerRef.current;
    if (!container) {
      return;
    }

    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
    }
  };

  return (
    <div className="play-page">
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        <meta name="keywords" content={metaData.keywords} />
        <meta property="og:title" content={metaData.ogTitle} />
        <meta property="og:description" content={metaData.ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaData.canonicalUrl} />
        <meta property="og:image" content={`https://logicpuzzles.ca${metaData.ogImage}`} />
        <meta property="og:image:width" content={metaData.ogImageWidth} />
        <meta property="og:image:height" content={metaData.ogImageHeight} />
        <meta property="og:image:alt" content={`${metaData.gameName} puzzle gameplay screenshot`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaData.twitterTitle} />
        <meta name="twitter:description" content={metaData.twitterDescription} />
        <meta name="twitter:image" content={`https://logicpuzzles.ca${metaData.ogImage}`} />
        <meta name="twitter:image:alt" content={`${metaData.gameName} puzzle gameplay screenshot`} />
        <link rel="canonical" href={metaData.canonicalUrl} />
        <meta name="robots" content="noindex, follow" />
        
        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {generateBreadcrumbData()}
        </script>
        
        {/* Game Structured Data */}
        <script type="application/ld+json">
          {generateGameStructuredData()}
        </script>
      </Helmet>

      <div ref={navRef}>
        <NavBar />
        <Breadcrumbs gameName={metaData.gameName} gamePath={gamePath} />
      </div>
      <div className="play-main">
        <div
          ref={gameContainerRef}
          className="play-stage"
          style={{ height: `calc(100vh - ${navHeight}px)` }}
        >
          <div className="play-toolbar">
            {printableGames[gamePath] && (
              <Link to={printableGames[gamePath]} className="play-tool">
                <svg className="play-tool__icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                Print
              </Link>
            )}
            {!isFullscreen && (
              <a
                href={platform === 'desktop'
                  ? '#get-the-app'
                  : storeUrl(appKey, platform === 'android' ? 'google_play' : 'app_store', 'play-pill', storePage)}
                target={platform === 'desktop' ? undefined : '_blank'}
                rel={platform === 'desktop' ? undefined : 'noopener'}
                onClick={() => {
                  if (platform !== 'desktop') {
                    trackStoreClick({ app: appKey, store: platform === 'android' ? 'google_play' : 'app_store', placement: 'play-pill', page: storePage });
                  }
                }}
                className="play-tool play-tool--spark"
              >
                <svg className="play-tool__icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="6" y="2" width="12" height="20" rx="2.5" />
                  <line x1="11" y1="18" x2="13" y2="18" />
                </svg>
                Get the app
              </a>
            )}
            <button
              type="button"
              onClick={handleFullscreenToggle}
              aria-label={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
              aria-pressed={isFullscreen}
              className={`play-tool${isFullscreen ? ' play-tool--exit' : ''}`}
            >
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              ) : 'Full Screen'}
            </button>
          </div>
          {isLoading && (
            <div className="play-loading">
              <div className="play-spinner" />
            </div>
          )}
          <iframe
            ref={frame.ref}
            src={`/${gamePath}/index.html`}
            title={metaData.gameName}
            className={`play-frame${iframeVisible ? ' is-visible' : ''}`}
            onLoad={frame.onLoad(handleIframeLoad)}
            allow="fullscreen"
            allowFullScreen
          />
        </div>

        {/* Web → app funnel */}
        <div className="play-below">
          <ProgressNote returnTo={`/play/${gamePath}`} />
          <StoreCTA
            id="get-the-app"
            app={appKey}
            variant="strip"
            placement="play-strip"
            page={storePage}
            headline={ctaHeadline(appKey, metaData.gameName)}
          />
        </div>

        {/* Banner Ad */}
        <AdSlot className="play-ad" domGuard />

        {/* SEO Content Section */}
        <div className="play-about">
          <h1>{metaData.ogTitle}</h1>
          <p>
            {metaData.description}
          </p>
        </div>

      </div>
    </div>
  );
}

export default GamePage; 